import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Module-level so moving between onboarding steps (or reopening the paywall)
// doesn't refetch within the same session.
let statsPromise = null;

const fetchCommunityStats = () => {
  if (!statsPromise) {
    statsPromise = Promise.resolve()
      .then(() => axios.get(`${API}/community-stats`))
      .then((res) => res?.data || null)
      .catch(() => {
        statsPromise = null; // allow a retry on the next mount
        return null;
      });
  }
  return statsPromise;
};

/**
 * Drop the memoized request.
 *
 * Exists for tests: the cache above is module-level by design, and
 * jest.resetModules() cannot clear it without also handing the test a second
 * copy of React. Not used by application code.
 */
export const resetCommunityStatsCache = () => {
  statsPromise = null;
};

/**
 * Live community counts for the paywall's social proof.
 *
 * Same contract as useStripePrices: the number always comes from the
 * backend, and there is no hardcoded fallback. A figure like "122 husbands"
 * is a factual claim made to someone deciding whether to pay — a stale or
 * invented one is worse than showing nothing, so callers render the block
 * only once `husbands` is a real number.
 */
export function useCommunityStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchCommunityStats().then((data) => {
      if (!cancelled && data) setStats(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const husbands = Number(stats?.husbands) || null;
  const gestures = Number(stats?.gestures) || null;

  return { husbands, gestures, loading: stats === null };
}

export default useCommunityStats;
